import PropTypes from 'prop-types';
import React, { useState } from 'react';
import injectSheet from 'react-jss';
import { Link } from 'react-router-dom-v5-compat';

import { activateUserAccount } from '~/api/login';
import {
  black2,
  evisortBlue,
  evisortRed,
  evisortYellow,
  semibold,
} from '~/assets/shared-styles/general';
import LoginPageContainer from '~/components/LoginPage/LoginPageContainer/LoginPageContainer';
import LoginPassword from '~/components/LoginPage/LoginPassword/LoginPassword';
import { getDecodedUserInfoFromLoginToken } from '~/components/LoginPage/utils';
import { trackSegment } from '~/components/SegmentAnalytics';
import LoadingSpinner from '~/components/Shared/Icons/LoadingSpinner';
import { Markdown } from '~/eds/src/components/Markdown/Markdown';
import { LoginViewType } from '~/enums/LoginViewType';
import { QueryParamType } from '~/enums/QueryParamType';
import { useAsync } from '~/hooks/useAsync';
import { usePasswordsWithValidation } from '~/hooks/usePasswordsWithValidation';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { getPageSearchQueryByKey } from '~/utils/searchQuery';

const componentsOverride = {
  a: (props) => (
    <Link {...props} to={props.href}>
      {props.children}
    </Link>
  ),
};
function ErrorMessageView({ classes }) {
  const intl = useIntl();

  return (
    <div className={classes.errorMessage}>
      <Markdown
        components={componentsOverride}
        text={intl.formatMessage({
          id: 'pages.activation.errorMessage',
          defaultMessage:
            "An error ocurred. Please <a href='/'>go here</a> and try again.",
          description: 'Generic error message when server returns an error',
        })}
      />
    </div>
  );
}

function ChangedPasswordView() {
  const intl = useIntl();
  return (
    <Markdown
      components={componentsOverride}
      text={intl.formatMessage({
        id: 'pages.activation.changePasswordLink',
        defaultMessage:
          "You have successfully changed your password. You will be redirected to the platform. If not, go to <a href='/'>login page</a> and login to the platform.",
        description: 'Success message when the token is valid',
      })}
    />
  );
}

function NoTokenView({ classes }) {
  const intl = useIntl();
  return (
    <div className={classes?.errorMessage}>
      {intl.formatMessage({
        id: 'pages.activation.tokenError',
        defaultMessage: 'No valid reset password link found.',
        description: 'Error message when the token is invalid',
      })}
    </div>
  );
}

function FormView({
  classes,
  firstName,
  setFirstNameChange,
  lastName,
  setLastNameChange,
  jobTitle,
  setJobTitleChange,
  submitResetPassword,
  ...rest
}) {
  const { validationResult } = rest;
  const intl = useIntl();
  return (
    <div className={classes.formContainer}>
      <form onSubmit={submitResetPassword}>
        <label className={classes.loginFormLabel} htmlFor="firstName">
          {intl.formatMessage({
            id: 'pages.activation.firstName',
            defaultMessage: 'First name',
            description: 'Label for first name input',
          })}
        </label>
        <div className={classes.inputContainer}>
          <input
            autoFocus={true}
            className={classes.textInput}
            defaultValue={firstName}
            id="firstName"
            type="text"
            onChange={(e) => setFirstNameChange(e.target.value)}
          />
        </div>
        <label className={classes.loginFormLabel} htmlFor="lastName">
          {intl.formatMessage({
            id: 'pages.activation.lastName',
            defaultMessage: 'Last name',
            description: 'Label for last name input',
          })}
        </label>
        <div className={classes.inputContainer}>
          <input
            defaultValue={lastName}
            id="lastName"
            type="text"
            onChange={(e) => setLastNameChange(e.target.value)}
            className={classes.textInput}
          />
        </div>
        <label className={classes.loginFormLabel} htmlFor="jobTitle">
          {intl.formatMessage({
            id: 'pages.activation.jobTitle',
            defaultMessage: 'Job title',
            description: 'Label for job title input',
          })}
        </label>
        <div className={classes.inputContainer}>
          <input
            className={classes.textInput}
            defaultValue={jobTitle}
            id="jobTitle"
            type="text"
            onChange={(e) => setJobTitleChange(e.target.value)}
          />
        </div>
        <LoginPassword
          passwordLabel={intl.formatMessage({
            id: 'pages.activation.createPassword',
            defaultMessage: 'Create password',
            description: 'Label for password input',
          })}
          confirmPasswordLabel={intl.formatMessage({
            id: 'pages.activation.verifyPassword',
            defaultMessage: 'Verify password',
            description: 'Label for confirm password input',
          })}
          {...rest}
        />

        <div className={classes.footnote}>
          <FormattedMessage
            id="pages.activation.termsOfService"
            defaultMessage="By clicking below, you confirm that you agree to use the platform in accordance with the Evisort <link>Terms of Service.</link>"
            description="Terms of service link"
            values={{
              link: (chunks) => (
                <a
                  href="https://www.evisort.com/terms/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {chunks}
                </a>
              ),
            }}
          />
        </div>
        <div className={classes.formButtonContainer}>
          <button
            className={classes.formButton}
            disabled={!validationResult.allValid}
            type="submit"
            onClick={submitResetPassword}
          >
            {intl.formatMessage({
              id: 'pages.activation.startUsingEvisort',
              defaultMessage: 'Start using Evisort',
              description: 'Button text to start using Evisort | maxLength=60',
            })}
          </button>
        </div>
      </form>
    </div>
  );
}

function Page(props) {
  const { classes } = props;

  const token = getPageSearchQueryByKey(QueryParamType.Token);
  const slug = getPageSearchQueryByKey(QueryParamType.Slug);
  const tokenType = getPageSearchQueryByKey(QueryParamType.StytchTokenType);
  const intl = useIntl();
  const formatDate = useFormatDate();

  const [
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    validationResult,
  ] = usePasswordsWithValidation();
  const [view, setView] = useState();
  const [firstName, setFirstNameChange] = useState();
  const [lastName, setLastNameChange] = useState();
  const [jobTitle, setJobTitleChange] = useState();

  const formData = {
    first_name: firstName,
    last_name: lastName,
    job_title: jobTitle,
    new_password: password,
  };

  function submitResetPassword(e) {
    e.preventDefault();
    if (!token) setView(LoginViewType.TokenMissing);
    else {
      setView(LoginViewType.FormSubmitting);
      handleFirstTimeLoginFormSubmit();
    }
  }

  const { executor: handleFirstTimeLoginFormSubmit } = useAsync(
    activateUserAccount,
    { token, slug, tokenType, formData },
    {
      successHandler: async (response) => {
        setView(LoginViewType.FormSubmitted);
        if (response?.msg) {
          const redirectUrl = response.msg;

          try {
            if (response.user_id || response.client_id) {
              trackSegment('User First Login', {
                groupId: response.client_id,
                userId: `${response.user_id}_${response.client_id}`,
                firstLoginDate: formatDate(new Date(), 'utc'),
              });
            } else {
              const { client_id, user_id } =
                getDecodedUserInfoFromLoginToken(redirectUrl);

              trackSegment('User First Login', {
                groupId: client_id,
                userId: `${user_id}_${client_id}`,
                firstLoginDate: formatDate(new Date(), 'utc'),
              });
            }
          } catch (error) {
            console.error(error);
          }
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 1000);
        }
      },
      errorHandler: () => {
        setView(LoginViewType.Error);
      },
    },
  );

  function renderView() {
    switch (view) {
      case LoginViewType.Error:
        return <ErrorMessageView classes={classes} />;
      case LoginViewType.FormSubmitting:
        return <LoadingSpinner />;
      case LoginViewType.FormSubmitted:
        return <ChangedPasswordView />;
      case LoginViewType.TokenMissing:
        return <NoTokenView classes={classes} />;
      default:
        return (
          <FormView
            classes={classes}
            firstName={firstName}
            setFirstNameChange={setFirstNameChange}
            lastName={lastName}
            setLastNameChange={setLastNameChange}
            jobTitle={jobTitle}
            setJobTitleChange={setJobTitleChange}
            submitResetPassword={submitResetPassword}
            password={password}
            setPassword={setPassword}
            confirmPassword={confirmPassword}
            setConfirmPassword={setConfirmPassword}
            validationResult={validationResult}
          />
        );
    }
  }

  return (
    <LoginPageContainer
      title={intl.formatMessage({
        id: 'pages.activation.welcomeToEvisort',
        defaultMessage: 'Welcome to Evisort!',
        description: 'Title for the activation page',
      })}
    >
      {renderView()}
    </LoginPageContainer>
  );
}

Page.propTypes = {
  classes: PropTypes.object.isRequired,
};

const loginFormLabel = {
  display: 'block',
  fontSize: '15px',
  fontWeight: semibold,
  textAlign: 'left',
};

const styles = {
  formContainer: {
    margin: '40px auto',
    width: '360px',
    '& a': {
      color: evisortBlue,
      textDecoration: 'none',
      '&:visited': {
        color: evisortBlue,
      },
    },
  },

  formButtonContainer: {
    marginTop: '40px',
  },

  formButton: {
    padding: '10px 20px',
    width: '350px',
    display: 'inline-block',
    backgroundColor: evisortYellow,
    borderRadius: '3px',
    border: 'solid 1px rgba(0, 0, 0, 0.25)',
    color: black2,
    fontSize: '15px',
    cursor: 'pointer',
    textDecoration: 'none',
    '&:disabled': {
      opacity: 0.5,
    },

    '&:focus': {
      outline: 'none',
    },
  },

  errorMessage: {
    margin: '20px',
    color: evisortRed,
    fontSize: '15px',
  },

  loginFormLabel: {
    ...loginFormLabel,
  },

  loginFormLabelDisabled: {
    ...loginFormLabel,
    opacity: 0.5,
  },

  textInput: {
    margin: '10px 0',
    padding: '10px',
    width: '360px',
    border: '1px solid #ccc',
    borderRadius: '3px',
    color: black2,
    fontSize: '16px',
    '&:placeholder': {
      opacity: 0.5,
    },

    '&:focus': {
      outline: 'none',
    },
  },

  inputContainer: {
    marginBottom: '12px',
    position: 'relative',
    display: 'inline-block',
  },

  footnote: {
    marginTop: '24px',
    fontSize: '12px',
    lineHeight: '16px',
    textAlign: 'left',
  },
};

export default injectSheet(styles)(Page);
